<template>
  <div :class="['app-wrapper', { 'is-active': isSearched }]">
    <nav v-if="isSearched" class="top-nav">
      <div class="logo-small">Search System</div>
    </nav>
    <main v-if="isSearched" class="layout-container">
      <aside class="side-panel left-panel">
        <div class="unified-sidebar flex-column">
          <div class="sidebar-section cell-section">
            <div class="section-title light-text">虚拟细胞</div>
            <div class="inner-card-white">
              <CellStructure :width="280" :height="240"></CellStructure>
            </div>
          </div>
          <div class="sidebar-section graph-section">
            <div class="section-title light-text">分子网络</div>
            <div class="inner-card-dark">
              <ForceGraph></ForceGraph>
            </div>
          </div>
        </div>
      </aside>
      <section class="main-content">
        <div v-if="loading" class="loading-container">
          <el-icon class="is-loading">
            <Loading />
          </el-icon>
          <p class="loading-text">正在检索生物分子数据库...</p>
        </div>
        <template v-else>
          <div v-for="(item, index) in chatHistory" :key="index" class="result-block">
            <component :is="getCardComponent(item._index)" :data="item" />
          </div>
          <Pagination v-if="chatHistory.length > 0" :current="query.page" :total="total" @change="handlePageChange" />
        </template>
      </section>

      <aside class="side-panel right-panel">
        <div class="unified-sidebar" style="position: relative;">
          <div class="title_box">Current View: #529 人布鲁氏杆菌 Protein Structure</div>
          <div class="component-wrapper">
            <PDBViewer pdbId="1A8M" />
          </div>
        </div>

      </aside>
    </main>

    <div class="search-section">
      <div v-if="!isSearched" class="hero">
        <div class="cell-icon-box">
          <CellStructure></CellStructure>
        </div>
        <h1 class="hero-title">
          <div>分子云</div>
          <div>Molecular Atlas</div>
        </h1>
      </div>

      <div class="input-container">
        <div class="input-box">
          <textarea v-model="query.q" @keydown.enter.prevent="handleSend" placeholder="输入任何问题..." rows="2"
            class="multi-line-input"></textarea>
          <button @click="handleSend" class="send-icon-btn">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div class="suggestions">
          <button v-for="tag in suggestions" :key="tag" @click="fastSearch(tag)"
            :class="{ 'is-active-tag': tag.value === query.database_name }">
            {{ tag.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
  import { ref, reactive, onMounted } from 'vue'
  import { useStore } from '@/Store/index.js'

  import CellStructure from "./comps/CellStructure.vue"
  import ForceGraph from "./comps/ForceGraph.vue"

  import TaxonomyCard from './comps/TaxonomyCard.vue'
  import PathogenicMicroorganismCard from './comps/PathogenicMicroorganismCard.vue'
  import RepertoireResearchCard from './comps/RepertoireResearchCard.vue'
  import GeneListCard from "./comps/GeneListCard.vue"

  import PDBViewer from './comps/PDBViewer.vue'
  import Pagination from './comps/Pagination.vue'

  import { get_species_list } from "@/api/index.js"
  import { ElMessage } from 'element-plus';

  const store = useStore()

  // ================= 状态 =================
  const isSearched = ref(false)
  const chatHistory = ref([])
  const total = ref(0)
  const loading = ref(false)

  const query = reactive({
    q: '',
    page: 1,
    limit: 10,
    database_name: ''
  })

  // ================= 初始化恢复 =================
  onMounted(() => {
    Object.assign(query, store.searchParams)
    if (query.q) {
      isSearched.value = true
      getSpeciesList()
    }
  })

  // ================= 组件映射 =================
  const suggestions = [
    { label: '分类学', value: 'taxonomy' },
    { label: '病原微生物', value: 'pathogenic_microorganism' },
    { label: '免疫库数据', value: 'ireceptor_v1_repertoire' },
    { label: '基因名称', value: 'genenames' }
  ]

  const cardComponents = {
    taxonomy: TaxonomyCard,
    pathogenic_microorganism: PathogenicMicroorganismCard,
    ireceptor_v1_repertoire: RepertoireResearchCard,
    genenames: GeneListCard
  }

  const getCardComponent = (index) => cardComponents[index] || TaxonomyCard;

  // ================= ⭐ 核心：统一同步函数 =================
  const syncStore = () => {
    store.setSearchParams({ ...query })
  }

  // ================= 查询 =================
  const handleSend = () => {
    if (!query.q.trim()) return
    isSearched.value = true
    // query.page = 1
    syncStore()
    getSpeciesList()
  }

  // ================= 快捷筛选 =================
  const fastSearch = (tag) => {
    query.database_name = tag.value === query.database_name ? '' : tag.value;
    query.page = 1;
    syncStore();
    getSpeciesList();
  }

  // ================= 请求 =================
  const getSpeciesList = async () => {
    loading.value = true
    syncStore()
    try {
      const res = await get_species_list({ ...query })
      chatHistory.value = res.data || []
      total.value = res.total || 0
    } catch (error) {
      console.error("查询失败：", error)
      ElMessage.error("查询失败，请稍后重试")
    } finally {
      loading.value = false
    }
  }

  // ================= 分页 =================
  const handlePageChange = (p) => {
    query.page = p;
    syncStore();
    getSpeciesList();
    const container = document.querySelector('.main-content');
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
</script>

<style scoped>
  /* --- 1. 基础变量与重置 --- */
  .app-wrapper {
    --primary: #007AFF;
    --text-main: #1d1d1f;
    --text-sub: #86868b;
    --bg-color: #ffffff;
    --border-color: #e5e5e5;
    --card-bg: #f5f5f7;
    --panel-dark: #000d1a;

    min-height: 100vh;
    background-color: var(--bg-color);
    color: var(--text-main);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    display: flex;
    flex-direction: column;
  }

  /* --- 2. 初始/搜索中 状态通用布局 --- */
  .app-wrapper:not(.is-active) {
    justify-content: center;
    align-items: center;
  }

  .app-wrapper:not(.is-active) .search-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    text-align: center;
  }

  /* 布局容器 */
  .layout-container {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(400px, 800px) minmax(0, 1fr);
    gap: 32px;
    width: 100%;
    max-width: 1900px;
    margin: 0 auto;
    padding: 100px 40px 200px;
    align-items: start;
  }

  /* --- 3. 侧边栏与卡片 (合并逻辑) --- */
  .side-panel {
    height: 615px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    position: sticky;
    top: 100px;
  }

  .unified-sidebar {
    width: 100%;
    height: 100%;
    background: var(--bg-color);
    border-radius: 24px;
    border: 1px solid #dedede;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.18);
    padding: 15px;
    box-sizing: border-box;
    overflow: hidden;
  }

  /* 左侧面板深色定制 */
  .left-panel .unified-sidebar {
    background: var(--panel-dark);
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    gap: 10px;
  }

  .sidebar-section {
    width: 100%;
    border-radius: 22px;
  }

  .section-title {
    font-size: 15px;
    font-weight: 700;
    padding: 10px 0 0 10px;
  }

  .light-text {
    color: #ffffff;
    background-color: var(--panel-dark);
  }

  .sidebar-divider {
    width: 200px;
    height: 1px;
    background-color: #d8dad9;
  }

  /* --- 4. 搜索组件样式 --- */
  .is-active .search-section {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 20px 0 40px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(20px);
    display: flex;
    justify-content: center;
  }

  .input-container {
    width: 90%;
    max-width: 800px;
  }

  .input-box {
    display: flex;
    align-items: center;
    background: var(--card-bg);
    border-radius: 16px;
    padding: 10px 16px;
    border: 1px solid transparent;
    transition: all 0.3s;
  }

  .input-box:focus-within {
    background: #fff;
    border-color: var(--primary);
    box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.1);
  }

  .multi-line-input {
    flex: 1;
    border: none;
    background: transparent;
    padding: 8px;
    font-size: 17px;
    outline: none;
    resize: none;
    font-family: inherit;
    line-height: 1.5;
    min-height: 50px;
    color: var(--text-main);
  }

  .send-icon-btn {
    background: var(--text-main);
    color: white;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-bottom: 4px;
  }

  /* --- 5. 辅助组件 --- */
  .suggestions {
    display: flex;
    gap: 10px;
    margin-top: 15px;
    justify-content: center;
  }

  .suggestions button {
    background: #fff;
    border: 1px solid var(--border-color);
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
    color: var(--text-sub);
    cursor: pointer;
    transition: all 0.2s;
  }

  .suggestions button:hover,
  .suggestions button.is-active-tag {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  .top-nav {
    position: fixed;
    top: 0;
    width: 100%;
    padding: 15px 40px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 80px 0;
  }

  /* --- 6. 响应式与动画 --- */
  @media (max-width: 1200px) {
    .layout-container {
      grid-template-columns: 1fr;
    }

    .side-panel {
      display: none;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  .hero {
    display: flex;
    align-items: center;
    gap: 10px;
    animation: fadeIn 0.8s ease;
  }

  .hero-title {
    font-size: 42px;
    font-weight: 700;
    letter-spacing: -1px;
    margin: 0;
  }

  .result-block {
    margin-bottom: 20px;
  }
</style>